// src/services/vehicleService.js
import axios, { handleApiError } from '../utils/axios';

/**
 * Servicio para gestión de vehículos - Solo funcionalidades disponibles
 */
class VehicleService {
  
  /**
   * Obtener bitácora de vehículos con filtros y paginación
   * @param {Object} filters - Filtros de búsqueda
   * @param {string} filters.estado - Estado ('dentro', 'fuera')
   * @param {string} filters.fecha_desde - Fecha desde (YYYY-MM-DD)
   * @param {string} filters.fecha_hasta - Fecha hasta (YYYY-MM-DD)
   * @param {string} filters.placa - Búsqueda por placa
   * @param {number} filters.page - Número de página (empezando en 1)
   * @param {number} filters.per_page - Elementos por página
   * @returns {Promise<Object>} Lista paginada de vehículos
   */
  async obtenerBitacora(filters = {}) {
    try {
      // Configurar parámetros con valores por defecto
      const params = {
        page: filters.page || 1,
        per_page: filters.per_page || 50,
      };
      
      // Agregar filtros opcionales
      if (filters.estado) params.estado = filters.estado;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      if (filters.placa) params.placa = filters.placa;
      
      const response = await axios.get('api/vehiculos/listar', { params });
      
      return {
        success: true,
        data: response.data.vehiculos || response.data.data || [],
        pagination: {
          total: response.data.total || 0,
          page: response.data.page || 1,
          per_page: response.data.per_page || 50,
          total_pages: response.data.total_pages || 1,
          has_next: response.data.has_next || false,
          has_prev: response.data.has_prev || false,
        },
        message: response.data.message || 'Bitácora obtenida exitosamente',
      };
      
    } catch (error) {
      throw new Error(handleApiError(error, 'Error al obtener bitácora de vehículos'));
    }
  }

  /**
   * Buscar vehículos con filtros específicos
   * @param {Object} searchParams - Parámetros de búsqueda
   * @param {string} searchParams.placa - Número de placa
   * @param {string} searchParams.estado - Estado del vehículo
   * @param {string} searchParams.fecha_desde - Fecha desde
   * @param {string} searchParams.fecha_hasta - Fecha hasta
   * @returns {Promise<Object>} Lista de vehículos encontrados
   */
  async buscarVehiculos(searchParams = {}) {
      try {
        const params = {};
        
        // Solo agregar parámetros que tengan valor
        if (searchParams.placa && searchParams.placa.trim()) {
          params.placa = searchParams.placa.trim();
        }
        if (searchParams.estado) {
          params.estado = searchParams.estado;
        }
        if (searchParams.fecha_desde) {
          params.fecha_desde = searchParams.fecha_desde;
        }
        if (searchParams.fecha_hasta) {
          params.fecha_hasta = searchParams.fecha_hasta;
        }
        
        const response = await axios.get('api/vehiculos/buscar', { params });
        
        return {
          success: true,
          data: response.data.vehiculos || response.data.data || [],
          total: response.data.total || 0,
          message: response.data.message || 'Búsqueda completada',
        };
        
      } catch (error) {
        throw new Error(handleApiError(error, 'Error al buscar vehículos'));
      }
    }
    async obtenerDatosCobro(vehicleId) {
    try {
      const response = await axios.get(`api/vehiculos/${vehicleId}/datos-cobro`);
      return { success: true, data: response.data.data };
    } catch (error) {
      throw new Error(handleApiError(error, 'Error al obtener datos del cobro'));
    }
  }


  /**
   * Obtener TODOS los vehículos por rango de fechas (sin paginación)
   * @param {Object} filters - Filtros de búsqueda
   * @param {string} filters.estado - Estado ('dentro', 'fuera')
   * @param {string} filters.fecha_desde - Fecha desde (YYYY-MM-DD)
   * @param {string} filters.fecha_hasta - Fecha hasta (YYYY-MM-DD)
   * @param {string} filters.placa - Búsqueda por placa
   * @returns {Promise<Object>} Lista completa de vehículos
   */
  async obtenerTodosVehiculos(filters = {}) {
    try {
      const params = {};
      if (filters.estado) params.estado = filters.estado;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      if (filters.placa) params.placa = filters.placa;
      
      const response = await axios.get('api/vehiculos/listar-todos', { params });
      
      return {
        success: true,
        data: response.data.data || [],
        total: response.data.total || 0,
        timezone: response.data.timezone || 'America/Bogota',
        message: 'Vehículos obtenidos exitosamente',
      };
      
    } catch (error) {
      throw new Error(handleApiError(error, 'Error al obtener todos los vehículos'));
    }
  }

  /**
   * Obtener código QR por ID de vehículo
   * @param {string|number} vehicleId - ID del vehículo
   * @returns {Promise<Object>} Datos del QR
   */
  async obtenerQR(vehicleId) {
    try {
      const response = await axios.get(`api/vehiculos/${vehicleId}/qr`);
      
      return {
        success: true,
        qr_code: response.data.qr_code,
        qr_image: response.data.qr_image, // URL de la imagen QR
        vehicle_data: response.data.vehiculo,
        message: response.data.message || 'QR obtenido exitosamente',
      };
      
    } catch (error) {
      throw new Error(handleApiError(error, 'Error al obtener código QR'));
    }
  }

  /**
   * Registrar salida de vehículo
   * @param {string|number} vehicleId - ID del vehículo
   * @param {Object} exitData - Datos de salida (opcional)
   * @param {string} exitData.observaciones_salida - Observaciones de salida
   * @returns {Promise<Object>} Datos del vehículo actualizado
   */
  async registrarSalida(codigo_qr, exitData = {}) {
    try {
      const payload = {
        codigo_qr,
        tipo: "Manual", 
        observaciones_salida: exitData.observaciones_salida || "Salida Manual"
      };
      
      const response = await axios.post(`api/vehiculos/salida`, payload);
      
      return {
        success: true,
        data: response.data.data, // Cambié de response.data.vehiculo a response.data.data
        message: response.data.message || 'Salida registrada exitosamente',
      };

    } catch (error) {
      // Salida bloqueada porque hay que cobrar (grace period vencido, o QR de
      // plan/tarjeta que no cubre el momento) — no es un error de excepción,
      // el caller debe abrir el cobro para el vehículo indicado.
      if (error.response?.status === 403 && error.response?.data?.requiere_pago) {
        return {
          success: false,
          requierePago: true,
          error: error.response.data.error || 'Pago pendiente',
          vehiculo: error.response.data.data,
        };
      }
      throw new Error(handleApiError(error, 'Error al registrar salida del vehículo'));
    }
  }

  /**
   * Exportar bitácora a CSV
   * @param {Object} filters - Filtros para export (mismos que obtenerBitacora)
   * @returns {Promise<Blob>} Archivo CSV como blob
   */
  async exportarCSV(filters = {}) {
    try {
      const params = {};
      
      // Agregar filtros opcionales
      if (filters.estado) params.estado = filters.estado;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      if (filters.placa) params.placa = filters.placa;
      
      const response = await axios.get('api/vehiculos/export/csv', {
        params,
        responseType: 'blob', // Importante para archivos
      });
      
      return {
        success: true,
        blob: response.data,
        filename: response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'bitacora_vehiculos.csv',
      };
      
    } catch (error) {
      throw new Error(handleApiError(error, 'Error al exportar bitácora'));
    }
  }

  /**
   * Obtener estadísticas básicas (si está disponible)
   * @returns {Promise<Object>} Estadísticas generales
   */
  async obtenerEstadisticas() {
    try {
      const response = await axios.get('api/vehiculos/estadisticas');
      
      return {
        success: true,
        data: response.data,
        message: response.data.message || 'Estadísticas obtenidas',
      };
      
    } catch (error) {
      // Si no hay endpoint de estadísticas, calcular localmente
      console.warn('Endpoint de estadísticas no disponible, calculando localmente');
      
      try {
        const bitacora = await this.obtenerBitacora();
        const vehicles = bitacora.data;
        
        const vehiculos_dentro = vehicles.filter(v => v.estado === 'dentro').length;
        const hoy = new Date().toISOString().split('T')[0];
        const entradas_hoy = vehicles.filter(v => 
          v.fecha_entrada && v.fecha_entrada.startsWith(hoy)
        ).length;
        const salidas_hoy = vehicles.filter(v => 
          v.fecha_salida && v.fecha_salida.startsWith(hoy)
        ).length;
        
        return {
          success: true,
          data: {
            vehiculos_dentro,
            entradas_hoy,
            salidas_hoy,
            tiempo_promedio_permanencia: 0, // No calculable sin más datos
          },
          message: 'Estadísticas calculadas localmente',
        };
      } catch (statsError) {
        throw new Error('Error al obtener estadísticas');
      }
    }
  }
}

// Crear instancia singleton del servicio
const vehicleService = new VehicleService();

export default vehicleService;